// TotalAumWidget — household-level rollup card for the Home dashboard.
// Single KPI (Total AUM under management) + 12-month sparkline +
// small grid of supporting metrics. Org-scoped (platform owners see across
// orgs via the same /portfolio/org-rollup endpoint).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, Tooltip, YAxis } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMoneyCompact(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

export default function TotalAumWidget() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/portfolio/org-rollup`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e) {
        logger.warn('TotalAumWidget: load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Compute period change for sparkline
  let changePct = null;
  const series = data?.sparkline || [];
  if (series.length >= 2) {
    const start = series[0].totalValue;
    const end = series[series.length - 1].totalValue;
    if (start) changePct = ((end - start) / start) * 100;
  }
  const positive = (changePct || 0) >= 0;

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="total-aum-widget">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-[#4A3B32]" />
          </div>
          <div>
            <h3 className="font-heading text-base text-[#2C2420] leading-tight">Total AUM under management</h3>
            <p className="text-xs text-[#8C8078] mt-0.5">{data?.asOf ? `As of ${data.asOf}` : 'Aggregated across all tracked clients'}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex items-center justify-center" data-testid="total-aum-loading">
          <Loader2 className="w-5 h-5 text-[#4A3B32] animate-spin" />
        </div>
      ) : !data || data.totalAum === 0 ? (
        <p className="text-sm text-[#8C8078] py-6 text-center" data-testid="total-aum-empty">
          No portfolio data yet. Add holdings to a client to see this roll up.
        </p>
      ) : (
        <>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-4xl font-heading text-[#2C2420] tabular-nums" data-testid="total-aum-value">
                {fmtMoneyCompact(data.totalAum)}
              </p>
              {changePct !== null && (
                <p
                  className={cn('flex items-center gap-1 text-xs mt-1', positive ? 'text-green-700' : 'text-red-600')}
                  data-testid="total-aum-change"
                >
                  {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {positive ? '+' : ''}{changePct.toFixed(1)}% over last 12 months
                </p>
              )}
            </div>
            <div className="flex-1 max-w-[220px] h-14" data-testid="total-aum-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    formatter={(v) => fmtMoney(v)}
                    labelStyle={{ fontSize: 11 }}
                    contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                  />
                  <Line type="monotone" dataKey="totalValue" stroke={positive ? '#16A34A' : '#DC2626'} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Supporting metrics */}
          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#F0EAE3]">
            <Metric label="Clients tracked" value={data.clientsTracked} testId="aum-clients" />
            <Metric label="Accounts" value={data.totalAccounts} testId="aum-accounts" />
            <Metric label="Avg portfolio" value={fmtMoneyCompact(data.avgPortfolio)} testId="aum-avg" />
          </div>

          {/* Top 3 clients quick link */}
          {data.topClients?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#F0EAE3]">
              <p className="text-[10px] uppercase tracking-wide text-[#8C8078] mb-2">Top clients</p>
              <ul className="space-y-1.5" data-testid="aum-top-clients">
                {data.topClients.slice(0, 3).map((c) => (
                  <li key={c.clientId} className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => navigate(`/app/clients/${c.clientId}/portfolio`)}
                      className="text-[#2C2420] hover:text-[#A86A3A] truncate text-left"
                      data-testid={`aum-top-${c.clientId}`}
                    >
                      {c.name}
                    </button>
                    <span className="text-[#5D524D] tabular-nums shrink-0 ml-2">
                      {fmtMoneyCompact(c.marketValue)} <span className="text-[#8C8078]">· {c.weight.toFixed(1)}%</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-[#8C8078]">{label}</p>
      <p className="text-lg font-heading text-[#2C2420] mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}
